import firebase from 'firebase/compat/app';
import {
    Auth,
    User as FirebaseUser,
    getAuth,
    getIdToken,
    onAuthStateChanged,
    signInAnonymously,
    signInWithCustomToken,
    signOut,
    updateProfile,
} from 'firebase/auth';
import {
    Firestore,
    getFirestore,
    getDocs,
    collection,
    query,
    addDoc,
    doc,
    setDoc,
    QueryDocumentSnapshot,
    getDoc,
    DocumentSnapshot,
    deleteDoc,
    where,
    onSnapshot,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { fetchAndActivate, getRemoteConfig, getValue, RemoteConfig } from 'firebase/remote-config';

import IFirebaseDocument from './FirebaseDocument';
import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import Flow from '../../ui/arch/Flow';
import { IFirebaseRecord } from '../types/IFirebaseRecord.types';
import User from '../../domain/auth/User';
import { Chat, Message } from '../../domain/chats/Message.types';
import { Reminder } from '../../domain/sked/Reminder.types';
import { Feedback } from '../../domain/story/Feedback.types';
import { map } from '../../utils/map';
import { MyStory, StoryCodeInfo } from '../../types/Story.type';
import { MyTP } from '../../domain/reports/MyTP.types';
import { PermissionID, PermissionsResultRecord } from '../../types/Permissions.types';
import { Str } from '../../types/types.types';
import { ID } from '../../types/ID.types';
import { DAY, delay, MINUTE } from '../../utils/time';
import { Claims, ClaimResult } from '../../types/User.types';
import { CachePolicy } from '../../types/CachePolicy.types';
import { Collector, IFlow, IStateFlow } from '../../ui/arch/IFlow';
import { Void } from '../../domain/utils/Funcs.types';

const STORAGE_BASE = 'https://storage.googleapis.com';

type FirebaseConfig = {
    apiKey: string;
    projectId: string;
    authDomain: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
    databaseURL?: string;
};

const mainFirebaseConfig = {
    apiKey: 'AIzaSyBzUFGsZyXnd6oAefE-i_IukI8Ik28H3Wk',
    authDomain: 'mille-lenstour.firebaseapp.com',
    databaseURL: 'https://mille-lenstour-default-rtdb.firebaseio.com',
    projectId: 'mille-lenstour',
    storageBucket: 'mille-lenstour.appspot.com',
    messagingSenderId: '1086641762144',
    appId: '1:1086641762144:web:4709a16a8f1116dd7db805',
    measurementId: 'G-WENMZ77DD9',
};

const devFirebaseConfig = {
    apiKey: 'AIzaSyDJaPqFreOEf3JMAMvBCk8eV9zSc14mcvQ',
    projectId: 'clyps-app-dev',
    authDomain: 'clyps-app-dev.firebaseapp.com',
    storageBucket: 'clyps-app-dev.firebasestorage.app',
    messagingSenderId: '503812236159',
    appId: '1:503812236159:web:9a30e28d07975de744704a',
    measurementId: 'G-YCZPW8XTKV',
};

export class AccountSwitchError extends Error {
    public token: string;
    constructor(token: string) {
        super('This credential is linked to an existing account.');
        this.name = 'AccountSwitchError';
        this.token = token;
    }
}

const isProd = (): boolean => {
    switch (window.location.host.toLowerCase()) {
        case 'clyps.app':
        case 'web.clyps.app':
        case 'www.clyps.app':
        case 'culios.app':
        case 'web.culios.app':
        case 'www.culios.app':
        case 'localhost:3100':
            return true;
        default:
            return false;
    }
};

const firebaseConfig: FirebaseConfig = isProd() ? mainFirebaseConfig : devFirebaseConfig;

class FirebaseSource {
    private app: firebase.app.App;
    private auth: Auth;
    private db: Firestore;
    private phoneRequestInfo?: PhoneRequestInfo;
    private emailRequestInfo?: EmailRequestInfo;
    private remoteConfig: RemoteConfig;

    userIdFlow: Flow<string | undefined>;

    constructor() {
        this.app = firebase.initializeApp(firebaseConfig);
        this.auth = getAuth(this.app);
        this.db = getFirestore(this.app);
        this.remoteConfig = getRemoteConfig(this.app);
        this.userIdFlow = new Flow<string | undefined>(undefined);

        this.remoteConfig.settings.minimumFetchIntervalMillis = isProd() ? DAY / 2 : MINUTE * 5;
    }

    generateId = (path: string) => {
        return doc(collection(this.db, path)).id;
    };

    getList = async <Item>(path: string, mapper: (record: IFirebaseRecord) => Item): Promise<Item[]> => {
        const q = query(collection(this.db, path));
        const snapShot = await getDocs(q);
        const result = new Array<Item>();

        snapShot.forEach((it) => {
            result.push(mapper({ ...it.data(), id: it.id }));
        });

        return result;
    };

    getItem = async <Item>(path: string, id: string, mapper: (record: IFirebaseRecord) => Item): Promise<Item> => {
        const ref = doc(this.db, path, id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
            throw Error(`Requested document does not exist. Id ${id}`);
        }
        return mapper({ id: snap.id, ...snap.data() });
    };

    deleteDocument = async (path: string, id: string) => {
        await deleteDoc(doc(this.db, path, id));
    };

    getCurrentUser = async () => map(await authorize(this.auth), (it) => toUser(it));

    getUser = async () => {
        return toUser((await authorize(this.auth)) ?? (await signInAnonymously(this.auth)).user);
    };

    getDocuments = async <Document extends IFirebaseDocument>(path: string, filters?: DocumentsFilter[]) => {
        const wheres = filters?.map(({ field, oper, value }) => where(field, oper, value)) ?? [];
        const q = query(collection(this.db, path), ...wheres);
        const snapShot = await getDocs(q);
        const result = new Array<Document>();

        snapShot.forEach((it) => {
            result.push(toFirebaseDocument(it));
        });

        return result;
    };

    documentsFlow = <Document extends IFirebaseDocument>(
        path: string,
        filters?: DocumentsFilter[]
    ): IFlow<Document[]> => {
        const flow = createStateFlow<Document[]>();
        const wheres = filters?.map(({ field, oper, value }) => where(field, oper, value)) ?? [];

        flow.unsubscribe = onSnapshot(query(collection(this.db, path), ...wheres), (snapShot) => {
            const data = new Array<Document>();
            snapShot.forEach((doc) => {
                data.push(toFirebaseDocument(doc));
            });
            flow.emit(data);
        });

        return flow;
    };

    documentFlow = <Document extends IFirebaseDocument>(path: string, documentId: string): IFlow<Document> => {
        const flow = createStateFlow<Document>();

        flow.unsubscribe = onSnapshot(doc(this.db, path, documentId), (doc) => {
            if (doc.exists()) {
                flow.emit(toFirebaseDocument(doc));
            }
        });

        return flow;
    };

    getDocument = async <Document extends IFirebaseDocument>(path: string, docId: string) => {
        const doc = await this.getDocumentOrNull<Document>(path, docId);
        if (doc === null) throw Error(`Requested document does not exist. Id ${docId}`);
        return doc;
    };

    getDocumentOrNull = async <Document extends IFirebaseDocument | null>(path: string, docId: string) => {
        const ref = doc(this.db, path, docId);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        return toFirebaseDocument<Document>(snap);
    };

    createDocument = async (path: string, data: any) => {
        var ref = await addDoc(collection(this.db, path), cleanDoc(data));
        return ref.id;
    };

    updateDocument = async (path: string, storyId: string, data: any) => {
        const docRef = doc(this.db, path, storyId);
        await setDoc(docRef, cleanDoc({ ...data }), { merge: true });
    };

    signInAnonymously = async () => {
        await signInAnonymously(this.auth);
    };

    uploadFile = async (
        path: string,
        data: File | Blob | Uint8Array,
        onProgress: (progress: number) => void = () => { }
    ) => {
        const storage = getStorage();
        const fileRef = ref(storage, path);
        const task = uploadBytesResumable(fileRef, data);

        const uploadPromise = new Promise<string>((resolve, reject) => {
            task.on(
                'state_changed',
                (snapshot) => {
                    switch (snapshot.state) {
                        case 'running':
                            if (snapshot.totalBytes > 0) {
                                const f = snapshot.bytesTransferred / snapshot.totalBytes;
                                onProgress(f);
                            }
                            break;
                        case 'success':
                            break;
                        case 'error':
                            break;
                        default:
                            reject(`Upload interrupted with state: ${snapshot.state}`);
                            break;
                    }
                },
                (error) => {
                    reject(error);
                },
                () => {
                    // Get download URL
                    resolve(`${STORAGE_BASE}/${firebaseConfig.storageBucket}${path}`);
                }
            );
        });

        return await uploadPromise;
    };

    submitPhone = async (phone: string) => {
        const { requestId } = await this.call<{ requestId: string }>('sendSmsCode', { phone });
        this.phoneRequestInfo = { requestId, phone };
    };

    submitCode = async (code: string) => {
        const request = {
            phone: this.phoneRequestInfo?.phone,
            requestId: this.phoneRequestInfo?.requestId,
            code,
        };

        const { token, isSwitch } = await this.call<VerifyCodeResult>('verifySmsCode', request);

        if (isSwitch) {
            throw new AccountSwitchError(token);
        }

        await this.signInWithToken(token);
    };

    submitEmail = async (email: string) => {
        const { requestId } = await this.call<{ requestId: string }>('sendEmailCode', { email });
        this.emailRequestInfo = { requestId, email };
    };

    submitEmailCode = async (code: string) => {
        const request = {
            email: this.emailRequestInfo?.email,
            requestId: this.emailRequestInfo?.requestId,
            code,
        };

        const response = await this.call<VerifyCodeResult>('verifyEmailCode', request);

        if (response.isSwitch) {
            throw new AccountSwitchError(response.token);
        }

        await this.signInWithToken(response.token);
    };

    call = async <T>(fnName: string, args: object): Promise<T> => {
        const calledfn = httpsCallable(getFunctions(), fnName);
        const r = await calledfn(args);

        if (r?.data) {
            const error = (r.data as { error?: string }).error;
            if (error) throw new Error(error);
        }

        return r?.data as T;
    };

    applyForCreator = async () => {
        const r = await this.call<ClaimResult>('applyForCreator', {});
        if (r.state === 'approved') {
            await this.reauthorize();
            await this.getClaims('Network');
        }
        return r;
    };

    setClaims = (claims: Claims) => {
        sessionStorage.setItem('claims', JSON.stringify(claims));
    };

    getClaims = async (cachePolicy: CachePolicy = 'CacheOrNetwork'): Promise<Claims> => {
        let claims = sessionStorage.getItem('claims');

        if (cachePolicy === 'Cache') return JSON.parse(claims ?? '{}');
        if (!!claims && cachePolicy === 'CacheOrNetwork') return JSON.parse(claims);

        const result = await this.call<Claims>('getClaims', {});
        this.setClaims(result);
        return result;
    };

    signInWithToken = async (token: string) => {
        await signInWithCustomToken(this.auth, token);
        this.setClaims(await this.getClaims('Network'));
    };

    signOut = async () => {
        await signOut(this.auth);
    };

    addTp = async (storyId: string, tp: string) => {
        await this.call('addTp', { storyId, tp, source: 'web' });
    };

    addMyStory = async (storyId: string) => {
        const ADDED = 'added-stories';
        const added = window.sessionStorage.getItem(ADDED)?.split(',') ?? [];

        if (added.find((it) => it === storyId)) return;

        await this.getUser();
        await this.call('addMyStory', { storyId, source: 'web' });

        added.push(storyId);
        window.sessionStorage.setItem(ADDED, added.join(','));
    };

    removeMyStory = async (storyId: string) => {
        const user = await this.getUser();
        await this.deleteDocument(`my/${user.id}/stories`, storyId);
    };

    resetMyStoryChats = async (storyId: string) => {
        const user = await this.getUser();
        await this.updateDocument(`my/${user.id}/stories`, storyId, { chatCount: 0 });
    };

    sendMessage = async (toId: string, message: Message) => {
        await this.call('sendMessage', { recipientId: toId, message: message.text, url: message.url });
    };

    getChats = async () => {
        const user = await this.getCurrentUser();
        if (!user) throw new Error('Must be signed in to get messages');
        return await this.getDocuments<Chat>(`my/${user.id}/dms`);
    };

    getChatsFlow = (userId: string) => {
        if (!userId) throw new Error(`Missing user id`);
        return this.documentsFlow<Chat>(`my/${userId}/dms`);
    };

    setReminder = async (reminder: Reminder) => {
        const { time, ownerId, title, url, id } = reminder;
        const userId = (await this.getUser()).id;
        const path = `my/${userId}/reminders`;
        const docRef = doc(this.db, path, id);
        await setDoc(docRef, { date: time, ownerId, title, url });
    };

    sendFeedback = async (feedback: Feedback) => {
        await this.getUser();
        await this.call('sendFeedback', feedback);
    };

    train = async (storyId: string, url: string) => {
        await this.call('train', { storyId, url });
    };

    getStoryCode = async (code: string): Promise<StoryCodeInfo | null> => {
        return await this.getDocumentOrNull<StoryCodeInfo>(`code`, code);
    };

    setStoryCode = async (code: string, storyId: string): Promise<void> => {
        const userId = (await this.getCurrentUser())?.id;
        if (!userId) throw new Error('Login required for this action');
        const time = new Date().getTime();
        await this.updateDocument(`code`, code, { storyId, userId, time });
    };

    getMyStories = async (): Promise<MyStory[]> => {
        const user = await this.getCurrentUser();
        if (!user) return [];
        return await this.getDocuments<MyStory>(`my/${user?.id}/stories`);
    };

    getMyStory = async (id: string): Promise<MyStory | null> => {
        const user = await this.getCurrentUser();
        if (!user) return null;
        return await this.getDocumentOrNull<MyStory>(`my/${user?.id}/stories`, id);
    };

    getMyTps = (userId: string, minTime: number): IFlow<MyTP[]> => {
        return this.documentsFlow<MyTP & ID>(`my/${userId}/tps`, [{ field: 'time', oper: '>', value: minTime }]);
    };

    getPermissions = async (requester?: string | null): Promise<Partial<PermissionsResultRecord>> => {
        const user = await this.getCurrentUser();
        if (!user) return {};
        const web = await this.call<PermissionsResultRecord>('getPermissions', { requester });
        return web;
    };

    setAllowPermissions = async (requester: Str, permissionId: PermissionID, granted: boolean = true) => {
        const user = await this.getCurrentUser();
        if (!user) throw new Error('Cannot allow permissions by a guest user');
        await this.call('setPermissions', { requester, permissionId, granted });
    };

    updateName = async (displayName: string) => {
        await this.getUser();
        await updateProfile(this.auth.currentUser!, { displayName });
    };

    updatePhotoUrl = async (photoURL: string | null) => {
        await this.getUser();
        await updateProfile(this.auth.currentUser!, { photoURL });
    };

    getMessages = async (senderId: string) => {
        const user = await this.getCurrentUser();
        if (!user) return [];
        return await this.getDocuments<Message & ID>(`my/${user.id}/dms/${senderId}/messages`);
    };

    signInWithWebOtp = async (code: string) => {
        const customToken = await this.call<string>('getOtpCustomToken', { code });
        signInWithCustomToken(this.auth, customToken);
        await delay(0.5); // half second delay to let the sign in work
        return await this.getCurrentUser();
    };

    reauthorize = async () => {
        const current = await authorize(this.auth);
        if (!current) return;
        await getIdToken(current, true);
    };

    getConfig = async (key: string, defaultValue?: string): Promise<string | undefined> => {
        if (!window.sessionStorage.getItem('didFetch')) {
            await fetchAndActivate(this.remoteConfig);
            window.sessionStorage.setItem('didFetch', `${new Date().getTime()}`);
        }
        const value = getValue(this.remoteConfig, key)?.asString() ?? defaultValue;
        return value;
    };
}

const createStateFlow = <T>(
    initial?: T
): IStateFlow<T> & {
    collectors: Collector<T>[];
    onceCollectors: Collector<T>[];
    value: T | undefined;
    unsubscribe: Void | undefined;
} => ({
    emit: function (data: T): void {
        this.value = data;
        this.collectors.forEach((collector) => collector(data));
        this.onceCollectors.forEach((collector) => collector(data));
        this.onceCollectors = [];
    },
    collect: function (collector: Collector<T>): void {
        this.collectors.push(collector);
    },
    collectOnce: function (collector: Collector<T>): void {
        this.onceCollectors.push(collector);
    },
    data: function (): T | undefined {
        return this.value;
    },
    disconnect: function (collector: Collector<T>): void {
        this.collectors = this.collectors.filter((it) => it !== collector);
        this.onceCollectors = [];
        if (this.collectors.length === 0) {
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        }
    },
    collectors: [],
    onceCollectors: [],
    value: initial,
    unsubscribe: undefined,
});

const authorize = async (auth: Auth): Promise<FirebaseUser | null> => {
    return await new Promise((resolve) => {
        onAuthStateChanged(auth, (user) => {
            resolve(user);
        });
    });
};

const toFirebaseDocument = <Document>(doc: QueryDocumentSnapshot | DocumentSnapshot): Document => {
    return { id: doc.id, ...doc.data() } as Document;
};

const cleanDoc = (doc: any, level: number = 0) => {
    if (!doc) return;
    for (const s in doc) {
        if (doc[s] === undefined) {
            delete doc[s];
        } else if (typeof doc[s] === 'object') {
            cleanDoc(doc[s], level + 1);
        }
    }
    if (doc.hasOwnProperty('id') && level < 1) delete doc.id;
    return doc;
};

const toUser = (fbUser: FirebaseUser): User => {
    return {
        name: fbUser.displayName,
        email: fbUser.email,
        id: fbUser.uid,
        phone: fbUser.phoneNumber,
        isAnonymous: fbUser.isAnonymous,
        emailVerified: fbUser.emailVerified,
        photoUrl: fbUser.photoURL,
    };
};

export type DocumentsFilter = {
    field: string;
    oper: DocumentsFilterOperation;
    value: string | number | boolean;
};

type VerifyCodeResult = { userId: string; token: string; isSwitch?: boolean };

type PhoneRequestInfo = { phone: string; requestId: string };

type EmailRequestInfo = { email: string; requestId: string };

export type DocumentsFilterOperation = '==' | '>' | '<' | '>=' | '<=';

export default FirebaseSource;
